import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CircleDot, CalendarClock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import FiltrLista, { useFiltr, SkrytoFiltrem, PrazdnyFiltr } from "../components/FiltrLista";
import { useTym } from "../components/KdoUvidi";
import { Spinner } from "../components/ui/kit";
import { NStatusPill, N_STATUS_ORDER, N_STATUS_LABEL, nStav } from "../lib/nabidky";
import { fmtKc } from "../lib/format";
import { odpovida, dnes, plusDni } from "../lib/filtr";
import { poleTechnologie, poleClovek, poleKlient, poleHodnota, poleZmena, polePevne } from "../lib/filtryPole";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { PdfIkona } from "../components/PdfNahled";
import { useUrlStav } from "../lib/navigace";

const STAVY = [...N_STATUS_ORDER, "NEVYBRANA", "NEREALIZOVANA"];
const TECKA = { ROZPRACOVANA: "#f0a020", ODESLANA: "#2f6fd0", SCHVALENA: "#17876a", ZAMITNUTA: "#d93a2b",
  NAHRAZENA: "#a0a4aa", NEVYBRANA: "#a0a4aa", NEREALIZOVANA: "#d93a2b" };
const TVARY = ["nabídka", "nabídky", "nabídek"];
// Platnost (jako v PDF: od data verze) má smysl jen u nabídky, o které klient ještě nerozhodl.
const otevrena = (n) => ["ROZPRACOVANA", "ODESLANA"].includes(nStav(n));
const platnost = (n, d = dnes()) => {
  if (!otevrena(n) || !n.platnost_do) return [];
  if (n.platnost_do < d) return ["PROPADLA"];
  return n.platnost_do < plusDni(d, 7) ? ["KONCI"] : ["PLATNA"];
};

export default function Nabidky() {
  const nav = useNavigate();
  const { user } = useAuth();
  const tym = useTym();
  const [data, setData] = useState(null);
  const [q, setQ] = useUrlStav("q", "");
  const [pohled, setPohled] = useUrlStav("pohled", "vse");

  const load = useCallback(async () => {
    const { data } = await api.get("/api/nabidky", { params: { q } });
    setData(data);
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const pole = useMemo(() => [
    polePevne({ klic: "stav", nazev: "Stav", rod: "m", ikona: CircleDot, tvary: ["stav", "stavy", "stavů"],
      hodnoty: STAVY.map((s) => ({ v: s, label: N_STATUS_LABEL[s], tecka: TECKA[s] })), hodnotyPolozky: (n) => [nStav(n)] }),
    poleTechnologie((n) => (n.technologie ? [n.technologie] : [])),
    poleClovek({ klic: "obchodnik", nazev: "Obchodník", tym, me: user?.id, id: (n) => n.obchodnik_id }),
    poleKlient((n) => n.client_id, (n) => n.klient),
    poleHodnota((n) => n.zaklad),
    polePevne({ klic: "platnost", nazev: "Platnost", rod: "z", ikona: CalendarClock, tvary: ["stav", "stavy", "stavů"],
      hodnoty: [{ v: "PLATNA", label: "Platná" }, { v: "KONCI", label: "Končí do 7 dní" }, { v: "PROPADLA", label: "Propadlá" }],
      hodnotyPolozky: (n) => platnost(n) }),
    poleZmena((n) => n.updated_at),
  ], [tym, user?.id]);
  const [filtry, setFiltry] = useFiltr(pole);

  const items = useMemo(() => data?.items || [], [data]);
  const pohledy = useMemo(() => [
    { klic: "vse", nazev: "Všechny", test: () => true },
    { klic: "moje", nazev: "Moje", test: (n) => n.obchodnik_id === user?.id || (!n.obchodnik_id && n.autor_id === user?.id) },
    { klic: "cekaji", nazev: "Čekají na klienta", test: (n) => nStav(n) === "ODESLANA" },
    { klic: "propadle", nazev: "Propadlé", test: (n) => nStav(n) === "ODESLANA" && platnost(n)[0] === "PROPADLA" },
  ].map((p) => ({ ...p, pocet: items.filter(p.test).length })), [items, user?.id]);
  const rozsah = useMemo(() => items.filter((pohledy.find((p) => p.klic === pohled) || pohledy[0]).test), [items, pohledy, pohled]);
  const shown = useMemo(() => rozsah.filter((n) => odpovida(n, filtry, pole)), [rozsah, filtry, pole]);

  return (
    <div data-testid="nabidky-page">
      <PageHeader eyebrow="Obchod" title="Nabídky" subtitle="Cenové nabídky k technologiím." />

      <FiltrLista pole={pole} polozky={rozsah} filtry={filtry} setFiltry={setFiltry} tvary={TVARY}
        pohledy={pohledy} pohled={pohled} setPohled={setPohled} vysledek={shown.length} celkem={rozsah.length}
        hledani={{ q, setQ, placeholder: "Hledat nabídku, klienta…", testid: "nabidky-search" }} testid="nabidky-filtr" />

      <div className="card overflow-hidden">
        {!data ? <div className="p-10 flex justify-center text-muted"><Spinner /></div>
          : items.length === 0 ? <EmptyState icon="FileText" title={q ? "Nic nenalezeno" : "Žádné nabídky"}
            description={q ? "Zkus jiné hledání." : "Nabídku založíš na kartě technologie v obchodním případu."} />
          : shown.length === 0 ? (filtry.length ? <PrazdnyFiltr nazev="Žádná nabídka" onVymazat={() => setFiltry([])} />
            : <EmptyState icon="FileText" title="V tomhle pohledu nic není" description="Přepni na Všechny." />)
          : (<>
            <div className="hidden md:grid grid-cols-[110px_1.4fr_1fr_1fr_140px_130px_44px] gap-3 px-4 py-2.5 label-up text-muted border-b border-line">
              <span>Číslo</span><span>Název</span><span>Klient</span><span>Technologie</span><span>Celkem s DPH</span><span>Stav</span><span className="text-center">PDF</span>
            </div>
            {shown.map((n) => (
              <div key={n.id} role="link" tabIndex={0} onClick={() => nav(`/nabidky/${n.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") nav(`/nabidky/${n.id}`); }} data-testid={`nabidka-row-${n.id}`}
                className="w-full cursor-pointer text-left grid md:grid-cols-[110px_1.4fr_1fr_1fr_140px_130px_44px] grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-3.5 border-b border-line-softer last:border-0 hover:bg-inputbg focus-visible:outline-none focus-visible:bg-inputbg">
                <span className="amount text-[12px] text-muted hidden md:block">{n.cislo}</span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-700 text-ink truncate">{n.nazev}</span>
                  <span className="md:hidden text-[11.5px] text-muted">{n.klient} · {fmtKc(n.celkem_s_dph)} Kč</span>
                </span>
                <span className="hidden md:block text-[12.5px] text-sec truncate">{n.klient}</span>
                <span className="hidden md:block text-[12px] text-muted truncate">{n.technologie}</span>
                <span className="amount text-[13px] font-700 text-ink hidden md:block">{fmtKc(n.celkem_s_dph)} Kč</span>
                <span className="justify-self-start md:justify-self-auto"><NStatusPill status={nStav(n)} /></span>
                <span className="justify-self-center"><PdfIkona pdf={n.pdf} testid={`nabidka-pdf-${n.id}`} /></span>
              </div>
            ))}
            <SkrytoFiltrem skryto={filtry.length ? rozsah.length - shown.length : 0} tvary={TVARY} onZobrazit={() => setFiltry([])} />
          </>)}
      </div>
    </div>
  );
}
